import api from './api';

export interface CreateGrnPayload {
  gateEntryId: number;
  clientLocation?: string;
}

export interface CreateGrnItemPayload {
  productCategoryId?: number;
  productTypeId?: number;
  productMasterId?: number;
  itemCode?: string;
  unitType: string;
  weightPerItem?: number;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  palletCount: number;
  requiredPalletCount?: number;
  storageType: 'ground' | 'rack';
  storageDetails: unknown[];
  isOversized?: boolean;
  palletSizeSqft?: number;
  shortExcess: number;
  totalWeight: number;
  batchNo?: string;
  skuCode?: string;
  customerSapCode?: string;
  mfgDate?: string;
  expiryDate?: string;
  remarks?: string;
  lbdStorageMode?: 'dedicated' | 'shared' | null;
  lbdPalletCount?: number | null;
  lbdRemark?: string | null;
  lbdSharedPalletRef?: string | null;
  lbdStorageDetails?: unknown[] | null;
}


export const grnService = {
  // Check if GRN exists for gate entry
  getByGateEntry: async (gateEntryId: number) => {
    const response = await api.get(`/grn/by-gate-entry/${gateEntryId}`);
    return response.data;
  },

  // Distinct previously-used Supplier Name / address values, for the Inward
  // form's autocomplete suggestions
  getSupplierLocationSuggestions: async (search?: string): Promise<string[]> => {
    const response = await api.get('/grn/supplier-locations', { params: search ? { search } : undefined });
    return (response.data as any)?.data ?? [];
  },

  // Get GRN list (supports filtering and approvals)
  getGrnList: async (params?: { status?: string; page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/grn', { params });
    return response.data;
  },

  // Create GRN
  createGrn: async (payload: CreateGrnPayload) => {
    const response = await api.post('/grn', payload);
    return response.data;
  },

  // Get GRN by ID
  getGrn: async (id: number) => {
    const response = await api.get(`/grn/${id}`);
    return response.data;
  },

  // Add item to GRN
  addItem: async (grnId: number, payload: CreateGrnItemPayload) => {
    const response = await api.post(`/grn/${grnId}/items`, payload);
    return response.data;
  },

  // Get GRN items
  getItems: async (grnId: number) => {
    const response = await api.get(`/grn/${grnId}/items`);
    return response.data;
  },

  // Confirm GRN
  confirmGrn: async (grnId: number, data?: { clientLocation?: string; grnRemarks?: string; vehicleNo?: string; driverName?: string; driverPhone?: string; transporterName?: string; lrNumber?: string; lrDate?: string }) => {
    const response = await api.post(`/grn/${grnId}/confirm`, data);
    return response.data;
  },

  // Update GRN item
  updateItem: async (grnId: number, itemId: number, payload: Partial<CreateGrnItemPayload>) => {
    const response = await api.patch(`/grn/${grnId}/items/${itemId}`, payload);
    return response.data;
  },

  // Delete GRN item
  deleteItem: async (grnId: number, itemId: number) => {
    const response = await api.post(`/grn/${grnId}/items/${itemId}/delete`);
    return response.data;
  },

  // Approve GRN
  approveGrn: async (grnId: number) => {
    const response = await api.post(`/grn/${grnId}/approve`);
    return response.data;
  },

  // Reject GRN
  rejectGrn: async (grnId: number, reason?: string) => {
    const response = await api.post(`/grn/${grnId}/reject`, { reason });
    return response.data;
  },

  // ---- Past GRN update workflow (edit approved GRNs) ----
  // The global ResponseInterceptor wraps every response as { data, ... }, so we
  // return `response.data` to get the actual payload (same as getItems above).

  // Submit a request to edit an approved GRN
  createPastGrnUpdate: async (
    grnId: number,
    payload: PastGrnUpdatePayload,
  ): Promise<any> => {
    const response = await api.post(`/grn/${grnId}/past-updates`, payload);
    return response.data;
  },

  // List past GRN update requests (Admin/Super Admin only, enforced by backend)
  listPastGrnUpdates: async (status: string = 'pending'): Promise<any> => {
    const response = await api.get('/grn/past-updates', { params: { status } });
    return response.data;
  },

  // Get a single past GRN update request with diff detail
  getPastGrnUpdate: async (updateId: number): Promise<any> => {
    const response = await api.get(`/grn/past-updates/${updateId}`);
    return response.data;
  },

  // Approve a past GRN update (Admin/Super Admin only). storageReassignments
  // is optional — only needed when the admin is re-placing an item's pallets.
  approvePastGrnUpdate: async (
    updateId: number,
    storageReassignments?: StorageReassignmentPayload[],
  ): Promise<any> => {
    const response = await api.post(`/grn/past-updates/${updateId}/approve`, {
      storageReassignments,
    });
    return response.data;
  },

  // Reject a past GRN update (Admin/Super Admin only)
  rejectPastGrnUpdate: async (
    updateId: number,
    rejectionReason: string,
  ): Promise<any> => {
    const response = await api.post(`/grn/past-updates/${updateId}/reject`, {
      rejectionReason,
    });
    return response.data;
  },

  // Reopen an approved (past) GRN for full re-editing on the GRN screen.
  // Reverses its inventory and sets it back to draft. Returns the GRN (incl.
  // gateEntryId) so the caller can navigate to the Open GRN screen.
  reopenPastGrn: async (grnId: number): Promise<any> => {
    const response = await api.post(`/grn/${grnId}/reopen-for-edit`);
    return response.data;
  },
};

export interface PastGrnItemEditPayload {
  grnItemId: number;
  productDescription?: string;
  productCategoryId?: number;
  productMasterId?: number;
  // New pallet placement for this item — omit to leave existing storage untouched.
  // Ground/zone: { zoneId, pallets }[]. Rack: { rackId, locationId }[] (must
  // also set storageType: 'rack' when using this shape).
  storageDetails?: ({ zoneId: number; pallets: number } | { rackId: number; locationId: number })[];
  storageType?: 'ground' | 'rack';
  // New LBD (damaged-goods) placement — omit to leave existing LBD storage
  // untouched. Same dual shape as storageDetails; which one applies is
  // inferred from the object shape (rackId present = rack), no separate
  // lbdStorageType field needed.
  lbdStorageDetails?: ({ zoneId: number; pallets: number } | { rackId: number; locationId: number })[];
  goodQty?: number;
  cdamQty?: number;
  damQty?: number;
  palletCount?: number;
  itemCode?: string;
  unitType?: string;
  weightPerItem?: number;
  totalWeight?: number;
  batchNo?: string;
  skuCode?: string;
  customerSapCode?: string;
  mfgDate?: string;
  expiryDate?: string;
  remarks?: string;
}

export interface PastGrnUpdatePayload {
  editReason: string;
  header?: {
    clientLocation?: string;
    grnRemarks?: string;
  };
  items: PastGrnItemEditPayload[];
}

// Admin-chosen replacement storage placement for one GRN item, sent when
// approving a Past GRN update. Ground/zone bulk placement only.
export interface StorageReassignmentPayload {
  grnItemId: number;
  placements: { zoneId: number; pallets: number }[];
}
